import { useSessionStore } from "@features/session";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { MixedItemTag } from "@memorang/types";
import { router } from "expo-router";
import { useCallback, useMemo } from "react";
import {
	getProethicaEntryShortLabel,
	getProethicaOrderedEntries,
	selectProethicaNextAvailableEntry,
} from "../helpers/proethica-module-list";
import { useProethicaModuleList } from "./useProethicaModuleList";

const resolveContinueLabel = (
	nextEntry: MixedItemTag | undefined,
	isResolvingPath: boolean,
): string => {
	if (nextEntry) {
		return `Continue to ${getProethicaEntryShortLabel(nextEntry)}`;
	}
	return isResolvingPath ? "Continue" : "Go home";
};

export const useProethicaNextModule = (currentModuleId?: string) => {
	const { data: mixedItemDist } = useMixedItemDist();
	const { startModule, startingModuleId, resolveModuleItems, liveStatusFor } =
		useProethicaModuleList();
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);

	// Pass the current module as treat-as-complete: its backend status can still
	// be stale right after the session ends, which would otherwise falsely lock
	// the entries it unlocks.
	const entries = useMemo(
		() =>
			resolveModuleItems(
				getProethicaOrderedEntries(mixedItemDist?.exams),
				currentModuleId,
			),
		[mixedItemDist?.exams, currentModuleId, resolveModuleItems],
	);

	const nextEntry = useMemo(
		() =>
			selectProethicaNextAvailableEntry(
				entries,
				currentModuleId,
				(entry) => liveStatusFor(entry) === "COMPLETE",
			),
		[entries, currentModuleId, liveStatusFor],
	);

	// An empty roster means the distribution hasn't landed yet, not that the path
	// is finished - continuing then would silently route home.
	const isResolvingPath = entries.length === 0;
	const continueGoesHome = !(isResolvingPath || nextEntry);

	const handleContinueToNextModule = useCallback(() => {
		if (startingModuleId != null || isResolvingPath) {
			return;
		}
		if (!nextEntry) {
			resetSessionStore();
			router.replace("/(protected)/(tabs)/home");
			return;
		}
		return startModule(String(nextEntry.id), { replace: true });
	}, [
		nextEntry,
		isResolvingPath,
		resetSessionStore,
		startModule,
		startingModuleId,
	]);

	return {
		nextEntry,
		isResolvingPath,
		continueLabel: resolveContinueLabel(nextEntry, isResolvingPath),
		continueGoesHome,
		handleContinueToNextModule,
		isContinueLoading: startingModuleId != null || isResolvingPath,
	};
};
